import type { V2 } from '../core/math';

export interface SpatialItem {
  readonly id: number;
  pos: V2;
  radius: number;
}

/**
 * Uniform-grid broad phase. Cheap enough to rebuild every tick for the few
 * hundred actors a zone holds; queries return candidates only, callers do the
 * exact shape test.
 */
export class SpatialHash<T extends SpatialItem> {
  private cells = new Map<number, T[]>();
  private readonly inv: number;

  constructor(readonly cellSize = 4) {
    this.inv = 1 / cellSize;
  }

  private key(cx: number, cz: number): number {
    // Pack two signed 16-bit cell coords into one number key.
    return ((cx + 32768) << 16) | (cz + 32768);
  }

  clear(): void {
    this.cells.clear();
  }

  insert(item: T): void {
    const r = item.radius;
    const x0 = Math.floor((item.pos.x - r) * this.inv);
    const x1 = Math.floor((item.pos.x + r) * this.inv);
    const z0 = Math.floor((item.pos.z - r) * this.inv);
    const z1 = Math.floor((item.pos.z + r) * this.inv);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const k = this.key(cx, cz);
        const cell = this.cells.get(k);
        if (cell) cell.push(item);
        else this.cells.set(k, [item]);
      }
    }
  }

  rebuild(items: Iterable<T>): void {
    this.clear();
    for (const it of items) this.insert(it);
  }

  /** Unique items whose cells overlap the query circle. */
  query(center: V2, radius: number, out: T[] = []): T[] {
    const seen = new Set<number>();
    const x0 = Math.floor((center.x - radius) * this.inv);
    const x1 = Math.floor((center.x + radius) * this.inv);
    const z0 = Math.floor((center.z - radius) * this.inv);
    const z1 = Math.floor((center.z + radius) * this.inv);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const cell = this.cells.get(this.key(cx, cz));
        if (!cell) continue;
        for (const it of cell) {
          if (seen.has(it.id)) continue;
          seen.add(it.id);
          out.push(it);
        }
      }
    }
    return out;
  }
}
